import { useAtomValue } from "jotai";
import {
    postClosingMetricsAtom,
    projectionsResolutionAtom,
} from "../../metrics.state";
import { useMetricClickHandler } from "../shared/useMetricClickHandler";
import {
    FinanceLabField,
    FinanceLabFieldType,
    formatFinanceLabValue,
} from "@/entities/FinanceLab";
import { Metric } from "@/entities/Finance/Finance.model";
import { applyWindowToMetrics } from "../../utils/applyWindowToMetric";
import dayjs from "dayjs";
import { HeaderWithTooltip } from "../../components/HeaderWithTooltip";

export interface SummaryTableRow {
    metric: string;
    yoyChange?: number | null;
    [key: string]: string | number | (() => void) | undefined | null;
    onClick?: () => void;
}

interface PeriodColumn {
    field: string;
    headerName: string;
    headerComponent?: any;
    headerComponentParams?: any;
}

export interface SummaryPostClosingMetricsResult {
    data: SummaryTableRow[];
    columns: PeriodColumn[];
}

function formatPeriodHeader(period: string): string {
    const date = dayjs(period);
    if (!date.isValid()) return period;

    if (period.length === 4) {
        return period;
    } else if (period.includes("Q")) {
        return period.replace("-", "-").slice(0, -2);
    } else {
        return date.format("MM-YY");
    }
}

function calculateYoYDelta(
    firstValue: number | null,
    lastValue: number | null,
): number | null {
    if (firstValue === null || lastValue === null || firstValue === 0) {
        return null;
    }
    const delta = (lastValue - firstValue) / Math.abs(firstValue);
    return delta;
}

function getMetric(
    metrics: Record<string, Metric>,
    field: string,
): Metric | undefined {
    return metrics[field];
}

export function useSummaryPostClosingMetricsData(): SummaryPostClosingMetricsResult {
    const handleMetricClick = useMetricClickHandler();
    const metricsProjectionsMetrics = useAtomValue(postClosingMetricsAtom);
    const projectionsResolution = useAtomValue(projectionsResolutionAtom);

    const windowedMetrics = applyWindowToMetrics(
        metricsProjectionsMetrics,
        projectionsResolution.window,
    );

    const interestCoverage = getMetric(
        windowedMetrics,
        FinanceLabField.DEAL_INTEREST_COVERAGE,
    );
    const debtToAssets = getMetric(
        windowedMetrics,
        FinanceLabField.DEAL_GROSS_DEBT_TO_ASSETS,
    );
    const debtToEbitda = getMetric(
        windowedMetrics,
        FinanceLabField.DEAL_GROSS_DEBT_TO_EBITDA,
    );
    const monthlyCashBurn = getMetric(
        windowedMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH_BURN_MONTHLY,
    );
    const dscr = getMetric(windowedMetrics, FinanceLabField.DEAL_DSCR);
    const debtToEquity = getMetric(
        windowedMetrics,
        FinanceLabField.DEAL_DEBT_TO_EQUITY,
    );
    const assetToEquity = getMetric(
        windowedMetrics,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS_TO_EQUITY,
    );

    const allPeriods = new Set<string>();
    [
        interestCoverage,
        debtToAssets,
        debtToEbitda,
        monthlyCashBurn,
        dscr,
        debtToEquity,
        assetToEquity,
    ].forEach((metric) => {
        metric?.values.forEach((v) => allPeriods.add(v.period));
    });

    const sortedPeriods = Array.from(allPeriods).sort();

    const periodColumns: PeriodColumn[] = sortedPeriods.map((period) => ({
        field: period,
        headerName: formatPeriodHeader(period),
    }));

    const columns: PeriodColumn[] = [
        { field: "metric", headerName: "" },
        ...periodColumns,
        {
            field: "yoyChange",
            headerName: "% Δ YoY",
            headerComponent: HeaderWithTooltip,
            headerComponentParams: {
                tooltipText:
                    "Shows the % difference from the current period over the previous period. Eg. if last 4 quarters is selected (Q4-24— Q3-25), the delta shows the difference between (Q4-24— Q3-25) and (Q4-23— Q3-24).",
            },
        },
    ];

    function createRow(
        label: string,
        metric: Metric | undefined,
        fieldType: FinanceLabFieldType,
    ): SummaryTableRow {
        const row: SummaryTableRow = {
            metric: label,
        };

        if (!metric) {
            sortedPeriods.forEach((period) => {
                row[period] = "-";
            });
            row.yoyChange = null;
            return row;
        }

        const valuesByPeriod = new Map(
            metric.values.map((v) => [v.period, v.value]),
        );

        sortedPeriods.forEach((period) => {
            const value = valuesByPeriod.get(period);
            row[period] =
                value !== undefined
                    ? formatFinanceLabValue(value, fieldType)
                    : "-";
        });

        const firstValue = valuesByPeriod.get(sortedPeriods[0]) ?? null;
        const lastValue =
            valuesByPeriod.get(sortedPeriods[sortedPeriods.length - 1]) ?? null;
        row.yoyChange = calculateYoYDelta(firstValue, lastValue);

        row.onClick = () => handleMetricClick(metric, label);

        return row;
    }

    const data: SummaryTableRow[] = [
        createRow(
            "Interest Coverage Ratio",
            interestCoverage,
            FinanceLabFieldType.RATIO,
        ),
        createRow(
            "Debt-to-Assets Ratio",
            debtToAssets,
            FinanceLabFieldType.RATIO,
        ),
        createRow(
            "Debt-to-EBITDA Ratio",
            debtToEbitda,
            FinanceLabFieldType.RATIO,
        ),
        createRow(
            "Monthly Cash Burn",
            monthlyCashBurn,
            FinanceLabFieldType.CURRENCY,
        ),
        createRow(
            "Debt Service Coverage Ratio",
            dscr,
            FinanceLabFieldType.RATIO,
        ),
        createRow(
            "Debt-to-Equity Ratio",
            debtToEquity,
            FinanceLabFieldType.RATIO,
        ),
        createRow(
            "Asset-to-Equity Ratio",
            assetToEquity,
            FinanceLabFieldType.RATIO,
        ),
    ];

    return { data, columns };
}
